/**
 * ============================================================================
 * KbSpaceDetailPage — Árvore de páginas + criação + busca FTS/semântica + Ask IA
 * ----------------------------------------------------------------------------
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.3.0 - Opção C: remove botão "Nova página" do header;
 *            criação de página passa a ser sempre feita a partir da árvore
 *            (dentro de uma coleção ou em "Sem coleção").
 *   RELEASE: 2026-07-08 v1.2.0 - Substitui lista chapada de páginas por
 *            KbSpaceCollectionsTree (árvore Coleções → Páginas com dnd).
 *   RELEASE: 2026-07-07 v1.1.0 - F3 Bloco 3/3: painel "Perguntar à IA".
 *   RELEASE: 2026-07-07 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Plus, Search, FileText, ArrowLeft, Loader2, Sparkles, Type,
  MessageCircleQuestion, Send, ChevronDown, ChevronUp, ShieldCheck,
} from "lucide-react";
import {
  useKbSpaces, useKbPagesTree, useCreateKbPage, useKbSearch,
} from "@/hooks/kb/useKb";
import { useKbSemanticSearch, useKbAsk, type KbSearchMode } from "@/hooks/kb/useKbSearch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { toast } from "sonner";
import { KbSpaceCollectionsTree } from "@/components/kb/KbSpaceCollectionsTree";
import { KbAclDialog } from "@/components/kb/KbAclDialog";


const toSlug = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

// Cores por status
const statusColor: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  in_review: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400",
  published: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  archived: "bg-red-500/15 text-red-700 dark:text-red-400",
};

/** Modo de busca: FTS puro (rápido, sem custo) ou Semântica/Híbrida (via IA). */
type SearchEngine = "fts" | "semantic";

export default function KbSpaceDetailPage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const { data: spaces = [] } = useKbSpaces();
  const space = useMemo(() => spaces.find((s: any) => s.id === spaceId), [spaces, spaceId]);

  const { data: pages = [], isLoading } = useKbPagesTree(spaceId);
  const createPage = useCreateKbPage();

  const [query, setQuery] = useState("");
  const [engine, setEngine] = useState<SearchEngine>("semantic");
  const [semanticMode] = useState<KbSearchMode>("hybrid");
  const [aclOpen, setAclOpen] = useState(false);

  // Busca FTS (sempre baixo custo) — usada quando engine=fts
  const { data: ftsResults = [], isFetching: ftsSearching } = useKbSearch(
    engine === "fts" ? query : "",
    spaceId ?? null,
  );
  // Busca semântica/híbrida via Edge Function — usada quando engine=semantic
  const semanticQuery = useKbSemanticSearch(
    engine === "semantic" ? query : "",
    spaceId ?? null,
    semanticMode,
  );
  const semanticResultsRaw = semanticQuery.data?.results ?? [];
  // Rede de proteção: esconde matches muito fracos mesmo que o backend deixe passar.
  const SEMANTIC_UI_FLOOR = 0.45;
  const semanticResults = semanticResultsRaw.filter((r: any) => {
    const s = typeof r.semantic_similarity === "number" ? r.semantic_similarity
            : typeof r.similarity === "number" ? r.similarity : 1;
    // Se veio via hybrid e tem fts_rank>0, mantém (casou por palavra-chave)
    if (typeof r.fts_rank === "number" && r.fts_rank > 0) return true;
    return s >= SEMANTIC_UI_FLOOR;
  });
  const semanticSearching = semanticQuery.isFetching;

  const results = engine === "fts" ? ftsResults : semanticResults;
  const searching = engine === "fts" ? ftsSearching : semanticSearching;

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");

  // F3 Bloco 3/3 — Painel "Perguntar à IA" (RAG com citações)
  const [askOpen, setAskOpen] = useState(false);
  const [askQuery, setAskQuery] = useState("");
  const askMutation = useKbAsk();
  const askResult = askMutation.data;

  const handleAsk = async () => {
    const q = askQuery.trim();
    if (q.length < 3) return;
    try {
      await askMutation.mutateAsync({ query: q, spaceId: spaceId ?? null, topK: 6 });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao consultar a IA");
    }
  };


  const handleCreate = async () => {
    if (!title.trim() || !spaceId) return;
    const id = await createPage.mutateAsync({
      space_id: spaceId,
      title: title.trim(),
      slug: toSlug(title),
    });
    setTitle(""); setOpen(false);
    navigate(`/tenant/kb/${spaceId}/pages/${id}`);
  };

  const isSearching = query.trim().length >= 2;

  return (
    <div className="flex h-full flex-col gap-4 p-4 md:p-6">
      {/* Header — apenas contexto do space. Criação de página fica na árvore. */}
      <div className="flex items-center gap-3 min-w-0">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/tenant/kb"><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold truncate">{space?.name ?? "Space"}</h1>
          <p className="text-xs text-muted-foreground truncate">
            {space?.description ?? "—"}
          </p>
        </div>
        {spaceId && (
          <Button variant="outline" size="sm" onClick={() => setAclOpen(true)}>
            <ShieldCheck className="h-4 w-4 mr-1" /> Acesso
          </Button>
        )}
      </div>

      {spaceId && (
        <KbAclDialog
          open={aclOpen}
          onOpenChange={setAclOpen}
          scope="space"
          scopeId={spaceId}
          title={space?.name}
        />
      )}

      {/* Busca + toggle FTS/Semântica */}
      <div className="flex flex-wrap items-center gap-2">

        <div className="relative flex-1 min-w-[260px] max-w-xl">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={
              engine === "semantic"
                ? "Busca inteligente (semântica + palavras-chave)…"
                : "Buscar por palavras-chave (FTS)…"
            }
            className="pl-8"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {searching && (
            <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
        <ToggleGroup
          type="single"
          value={engine}
          onValueChange={(v) => v && setEngine(v as SearchEngine)}
          size="sm"
        >
          <ToggleGroupItem value="semantic" aria-label="Busca semântica">
            <Sparkles className="h-3.5 w-3.5 mr-1" /> IA
          </ToggleGroupItem>
          <ToggleGroupItem value="fts" aria-label="Busca por palavras-chave">
            <Type className="h-3.5 w-3.5 mr-1" /> FTS
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* F3 Bloco 3/3 — Painel "Perguntar à IA" (RAG com citações) */}
      <Card className="overflow-hidden">
        <button
          type="button"
          onClick={() => setAskOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-2 text-sm font-medium">
            <MessageCircleQuestion className="h-4 w-4 text-primary" />
            Perguntar à IA
            <span className="text-xs text-muted-foreground font-normal">
              (RAG · usa somente conteúdo deste space)
            </span>
          </div>
          {askOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {askOpen && (
          <div className="border-t p-3 space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="Ex.: Como funciona o fluxo de aprovação de contratos?"
                value={askQuery}
                onChange={(e) => setAskQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAsk(); } }}
                maxLength={500}
                disabled={askMutation.isPending}
              />
              <Button
                size="sm"
                onClick={handleAsk}
                disabled={askMutation.isPending || askQuery.trim().length < 3}
              >
                {askMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <><Send className="h-4 w-4 mr-1" /> Perguntar</>
                )}
              </Button>
            </div>

            {/* Resposta */}
            {askResult && (
              <div className="space-y-2">
                <div className="rounded-md border bg-muted/30 p-3 text-sm whitespace-pre-wrap leading-relaxed">
                  {askResult.answer}
                </div>
                {askResult.citations.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-xs text-muted-foreground">Fontes:</div>
                    <div className="flex flex-wrap gap-1.5">
                      {askResult.citations.map((c) => (
                        <Link
                          key={`${c.index}-${c.page_id}`}
                          to={`/tenant/kb/${c.space_id}/pages/${c.page_id}`}
                          title={c.snippet}
                        >
                          <Badge variant="outline" className="hover:border-primary cursor-pointer text-[11px] gap-1">
                            <span className="font-semibold text-primary">[{c.index}]</span>
                            <span className="truncate max-w-[220px]">{c.title}</span>
                          </Badge>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground flex gap-3">
                  <span>modelo: {askResult.model}</span>
                  <span>tokens in/out: {askResult.usage.input_tokens}/{askResult.usage.output_tokens}</span>
                  {askResult.usage.latency_ms != null && (
                    <span>latência: {askResult.usage.latency_ms}ms</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>



      {/* Resultados de busca ou árvore */}
      {isSearching ? (
        <div className="flex-1 overflow-auto space-y-2">
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <span>{results.length} resultado{results.length === 1 ? "" : "s"}</span>
            {engine === "semantic" && semanticQuery.data?.config && (
              <Badge variant="outline" className="text-[10px]">
                {semanticQuery.data.config.model} · {semanticQuery.data.config.dimension}d
              </Badge>
            )}
            {engine === "semantic" && semanticQuery.isError && (
              <span className="text-destructive">
                Falha na busca semântica — tente FTS
              </span>
            )}
          </div>
          {results.length === 0 ? (
            <Card className="p-6 text-center text-sm text-muted-foreground">
              Nenhum conteúdo desta base responde bem a essa pergunta.
              Tente reformular ou alternar para busca <strong>FTS</strong>.
            </Card>
          ) : results.map((r: any) => {
            // Rótulo qualitativo do score (evita expor números confusos ao usuário)
            const simVal = typeof r.semantic_similarity === "number" ? r.semantic_similarity
                         : typeof r.similarity === "number" ? r.similarity : null;
            const isWeak = simVal !== null && simVal < 0.6 && !(typeof r.fts_rank === "number" && r.fts_rank > 0);
            const qualitative = simVal === null ? null
              : simVal >= 0.72 ? { label: "Alta relevância", cls: "text-emerald-600 dark:text-emerald-400" }
              : simVal >= 0.55 ? { label: "Média relevância", cls: "text-amber-600 dark:text-amber-400" }
              : { label: "Baixa relevância", cls: "text-muted-foreground" };
            return (
            <Link key={r.page_id} to={`/tenant/kb/${spaceId}/pages/${r.page_id}`}>
              <Card className="p-3 hover:border-primary transition-colors">
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate flex items-center gap-2">
                      {r.title}
                      {isWeak && (
                        <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-600 dark:text-amber-400">
                          match fraco
                        </Badge>
                      )}
                    </div>
                    <div
                      className="text-xs text-muted-foreground line-clamp-2 mt-0.5"
                      // FTS: snippet vem com <b>; Semântica: texto puro
                      dangerouslySetInnerHTML={{ __html: r.snippet ?? "" }}
                    />
                    {engine === "semantic" && qualitative && (
                      <div
                        className={`text-[10px] mt-1 ${qualitative.cls}`}
                        title={
                          [
                            typeof r.fused_score === "number" ? `score: ${r.fused_score.toFixed(3)}` : null,
                            simVal !== null ? `sim: ${(simVal * 100).toFixed(1)}%` : null,
                            typeof r.fts_rank === "number" ? `fts: ${r.fts_rank.toFixed(3)}` : null,
                          ].filter(Boolean).join(" · ")
                        }
                      >
                        {qualitative.label}
                      </div>
                    )}
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    {r.data_classification}
                  </Badge>
                </div>
              </Card>
            </Link>
            );
          })}
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          {/* Árvore Coleções → Páginas (com drag-and-drop) */}
          <KbSpaceCollectionsTree spaceId={spaceId!} />
        </div>
      )}
    </div>
  );
}
